import { getFromBreakpoint } from '@vakantiesnl/components/src/styles/breakpoints';
import { makeStyles } from '@vakantiesnl/components/src/styles/themes/themewrapper';

export const MIN_COLUMN_HEIGHT = 285;
export const COLUMN_DIFF_HEIGHT = 135;

/**
 * Get the column height, based on the rating
 * Max height for a column is 420px (285px + 135px)
 */
const getColumnHeight = (rating: number): string => `${MIN_COLUMN_HEIGHT + COLUMN_DIFF_HEIGHT * rating}px`;

export const useCustomStyles = makeStyles<{ rating: number }, 'priceBar' | 'selected' | 'lowest'>()(
	(theme, { rating }, classes) => ({
		columnContainer: {
			padding: theme.spacing.xxsmall,
			display: 'flex',
			// Fixed width to make sure we can also show skeleton/invisible pricebars without things jumping around
			width: 54,
			height: getColumnHeight(rating),

			[getFromBreakpoint('md')]: {
				// Fixed width to make sure we can also show skeleton/invisible pricebars without things jumping around
				width: 100,
			},
		},
		priceBarContainer: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			justifyContent: 'flex-end',

			'&:hover': {
				cursor: 'pointer',
			},
		},
		priceBar: {
			padding: `${theme.spacing.tiny} ${theme.spacing.tiny} ${theme.spacing.xsmall} ${theme.spacing.tiny}`,
			backgroundColor: theme.priceBar.background.default,
			color: theme.priceBar.foreground.default,
			borderRadius: theme.borderRadius.rounded,
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			textAlign: 'center',
			justifyContent: 'space-between',
			height: '100%',

			'&:hover': {
				backgroundColor: theme.priceBar.background.hover,
			},

			[getFromBreakpoint('md')]: {
				padding: theme.spacing.xsmall,
			},
		},
		selected: {
			[`.${classes.priceBar}`]: {
				backgroundColor: theme.priceBar.background.selected.default,
				color: theme.priceBar.foreground.selected,

				'&:hover': {
					backgroundColor: theme.priceBar.background.selected.hover,
				},
			},
		},
		lowest: {
			[`.${classes.priceBar}`]: {
				backgroundColor: theme.priceBar.background.lowest.default,
				color: theme.priceBar.foreground.default,

				'&:hover': {
					backgroundColor: theme.priceBar.background.lowest.hover,
				},
			},
		},
		priceLabel: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			rowGap: theme.spacing.xxsmall,
		},
		priceContainer: {
			marginBottom: theme.spacing.medium,
		},
		textFrom: {
			font: theme.priceBar.caption.sm,

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.caption.md,
				lineHeight: theme.lineHeight.spacious,
			},
		},
		textPrice: {
			font: theme.priceBar.price.sm,
			writingMode: 'vertical-lr',
			transform: 'rotate(180deg)',

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.price.md,
				writingMode: 'initial',
				transform: 'none',
			},
		},
		iconsContainer: {
			display: 'flex',
			flexDirection: 'column',
			columnGap: theme.spacing.tiny,
			marginBottom: theme.spacing.xxsmall,
			marginTop: 0,
			listStyle: 'none',

			[getFromBreakpoint('md')]: {
				flexDirection: 'row',
				justifyContent: 'center',
				rowGap: theme.spacing.xxsmall,
				marginBottom: theme.spacing.medium,
			},
		},
		tripDuration: {
			display: 'flex',
			flexDirection: 'column',
			rowGap: theme.spacing.xxsmall,
			alignItems: 'center',
		},
		textDays: {
			fontWeight: theme.weight.bold,
			font: theme.priceBar.caption.sm,
			whiteSpace: 'nowrap',

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.duration.md,
			},
		},
		textNight: {
			font: theme.priceBar.caption.sm,
			whiteSpace: 'nowrap',

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.caption.md,
			},
		},
		priceBarSelect: {
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			textAlign: 'center',
			height: '85px',
			justifyContent: 'space-between',

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.date.md,
			},
		},
		priceBarDate: {
			font: theme.priceBar.date.sm,
			/** Extra small fontsize for smaller then 320px breakpoints to prevent weird views */
			fontSize: theme.fontSize.tiny,

			[getFromBreakpoint('sm')]: {
				font: theme.priceBar.date.sm,
			},

			[getFromBreakpoint('md')]: {
				font: theme.priceBar.date.md,
			},
		},
		placeHolderPriceBarContainer: {
			'&:hover': {
				cursor: 'default',
			},
		},
		placeholderPriceBar: {
			backgroundColor: theme.skeleton.color,
		},
		placeholderPriceBarDate: {
			backgroundColor: theme.skeleton.color,
			borderRadius: theme.borderRadius.rounded,
			width: 70,
			height: 20,
		},
		invisiblePriceBar: {
			visibility: 'hidden',
		},
	}),
);
